// 학생 교재 진도(페이지) 디테일
import { normalizeAnswerConfirmed } from '@mathflat/domain/@entities/Answer/utils'
import { isNil } from 'lodash-es'
import { makeObservable, observable } from 'mobx'

import { DTO } from '../DTO'
import { WorkbookScoringDTO } from '../WorkbookScoring'

type ConstructorParams = {
  workbookType: 'public' | 'xistory' | 'custom' | 'signature' | 'school'
} & res.StudentWorkbookPageDetail

export class StudentWorkbookPageDetail extends DTO {
  id: number //교재 문제 id
  title: string //챕터 제목
  number: string //문항 순서
  level?: common.ProblemLevel //난이도
  conceptId?: number
  answerImageUrl?: string

  // getter로 통합 값 제공하기 때문에 private로 함
  // 시중교재이고 자이스토리가 아닐때
  private answer?: string // 시중 교재 답안
  private type?: common.ProblemType //문제 유형 SINGLE_CHOICE - 단답형 MULTIPLE_CHOICE - 선다형 SHORT_ANSWER-주관식 ESSAY - 서술형
  private problemId: res.StudentPublicWorkbookPageDetail['problemId'] // 저작권이 걸리지 않는 우리 디비에 있는 문제 id
  private problemUrl?: res.StudentPublicWorkbookPageDetail['problemUrl'] // 저작권이 걸리지 않는 우리 디비에 있는 문제 URL
  private solutionUrl?: res.StudentPublicWorkbookPageDetail['solutionImageUrl'] // 해설 이미지
  private video?: res.StudentSignatureWorkbookPageDetail['video'] // 문풀동
  isAnswerConfirmed = true // 시중교재 정답 지원 여부

  // 프론트에서 교재 종류 구별을 위해 넣는 값
  workbookType: ConstructorParams['workbookType']

  // 커스텀 교재일때(1)
  problem?: {
    id: number // 문제 정보 id
    conceptId: number // 개념 아이디
    type: common.ProblemType //문제 유형 SINGLE_CHOICE - 단답형 MULTIPLE_CHOICE - 선다형 SHORT_ANSWER-주관식 ESSAY - 서술형
    problemImageUrl: string // 문제 이미지 url
    answerImageUrl: string // 답 이미지 url
    solutionImageUrl: string // 해설 이미지 url
    answer: string // 답안
    autoScoredType: common.AutoScoredType // "POSSIBLE_PLUS" 자동 채점 가능한 문제에서 세부 타입
    hidden: boolean // 가려야 하는 문제 - 안쓰는값
    autoScored: boolean // 자동채점 문제인지
    video: res.StudentCustomWorkbookPageDetail['problem']['video']
    videoMatched: res.StudentCustomWorkbookPageDetail['problem']['videoMatched']
    level: res.StudentCustomWorkbookPageDetail['problem']['level']
    sourceType?: common.ProblemOriginType
  }
  // 자이스토리 교재일때
  xistory?: {
    description: string
    problem: {
      id: number
      conceptId: number
      type: string
      problemImageUrl: string // 문제 이미지 url
      answerImageUrl: string // 답 이미지 url
      solutionImageUrl: string // 해설 이미지 url
      answer: string // 답안
      autoScoredType: common.AutoScoredType // "POSSIBLE_PLUS" 자동 채점 가능한 문제에서 세부 타입
      hidden: boolean // 가려야 하는 문제 - 안쓰는값
      autoScored: boolean // 자동채점 문제인지
      video: NonNullable<
        NonNullable<res.StudentPublicWorkbookPageDetail['xistory']>['problem']
      >['video']
      videoMatched: NonNullable<
        NonNullable<res.StudentPublicWorkbookPageDetail['xistory']>['problem']
      >['videoMatched']
      level: NonNullable<
        NonNullable<res.StudentPublicWorkbookPageDetail['xistory']>['problem']
      >['level']
    }
  }

  // 점수
  scoring: WorkbookScoringDTO
  tagTop: res.Problem<'WORKBOOK'>['tagTop']
  constructor(data: ConstructorParams) {
    super()
    makeObservable(this, { scoring: observable })
    this.id = data.id
    this.title = data.title
    this.number = data.number
    this.workbookType = data.workbookType
    this.problemId = data.problemId
    this.scoring = new WorkbookScoringDTO(data.scoring)
    this.tagTop = data.tagTop || null

    // Only public
    this.type = 'type' in data ? data.type ?? undefined : undefined
    this.level = 'level' in data ? data.level ?? undefined : undefined
    this.xistory = 'xistory' in data ? data.xistory ?? undefined : undefined
    this.answer = 'answer' in data ? data.answer ?? undefined : undefined
    this.conceptId = 'conceptId' in data ? data.conceptId ?? undefined : undefined
    this.answerImageUrl = 'answerImageUrl' in data ? data.answerImageUrl ?? undefined : undefined
    if ('answerConfirmed' in data) {
      this.isAnswerConfirmed = normalizeAnswerConfirmed(data.answerConfirmed)
    }

    // Only custom
    this.problem = 'problem' in data ? data.problem ?? undefined : undefined

    // public & signature
    if ('problemUrl' in data) {
      this.problemUrl = data.problemUrl
    } else if ('problemImageUrl' in data) {
      this.problemUrl = data.problemImageUrl
      this.solutionUrl = data.solutionImageUrl
    } else {
      this.problemUrl = undefined
    }

    // Only signature
    this.video = 'video' in data ? data.video ?? undefined : undefined
  }

  // 교재별 각 타입의 타입을 가르킴 - type 대신 사용
  get problemType() {
    return (this.type ?? this.problem?.type) as
      | 'SINGLE_CHOICE'
      | 'MULTIPLE_CHOICE'
      | 'SHORT_ANSWER'
      | 'ESSAY'
  }

  get problemImageUrl() {
    const url =
      this.xistory?.problem.problemImageUrl ?? this.problemUrl ?? this.problem?.problemImageUrl

    // 없는 경우
    if (url === '0' || url === '-') {
      return undefined
    }
    return url
  }

  get problemAnswer() {
    const answer = this.answer ?? this.problem?.answer

    // 답이 없는 경우 (답이 이미지인 경우)
    if (answer === '.') return

    return answer
  }

  get solutionImageUrl() {
    const url =
      this.xistory?.problem.solutionImageUrl ?? this.solutionUrl ?? this.problem?.solutionImageUrl

    // 없는 경우
    if (url === '0' || url === '-') return

    return url
  }

  get workbookAnswerImageUrl() {
    const url = this?.answerImageUrl ?? this.problem?.answerImageUrl

    // 없는 경우
    if (url === '0' || url === '-') return

    return url
  }

  get workbookProblemId() {
    const id = this.problemId ?? this.problem?.id

    if (id === 0) return

    return id
  }

  get workbookConceptId() {
    return this.conceptId ?? this.problem?.conceptId
  }

  get isOverflowableImg() {
    if (this.problemType === 'SHORT_ANSWER') {
      return !!this.problemImageUrl
    }
    if (this.problemType === 'ESSAY') {
      return !!this.workbookAnswerImageUrl
    }
    return false
  }

  static from = (data: res.StudentWorkbookPageDetail) => {
    const workbookType = (() => {
      if ('xistory' in data && !isNil(data.xistory)) return 'xistory'
      else if ('problem' in data) return 'custom'
      else if ('solutionImageUrl' in data) return 'signature'
      else if ('answerConfirmed' in data) return 'public'
      else return 'school'
    })()
    return new StudentWorkbookPageDetail({ ...data, workbookType })
  }

  get answerVideo() {
    return this.xistory?.problem.video ?? this.video ?? this.problem?.video
  }

  get videoMatched() {
    if (!this.problem) return true
    return this.problem?.videoMatched
  }

  get workbookProblemVideo() {
    return this.video
  }
  get workbookProblemConceptId() {
    return this.conceptId
  }
}
